/**
 * InclusionCard — one card per inclusion (persisted or staged). Renders:
 *   - kind badge, node name (looked up via candidateIndex), node id
 *   - materialized counts scoped to this inclusion
 *   - FlagToggles for precache/prelaunch/multiplex
 *   - onlyDefaultVersion checkbox (app/project/org scope only)
 *   - exceptions: list of `exclude` and `override` chips inside the subtree
 *   - stale badge + remove affordance when the inclusion is stranded
 *   - remove button for the whole inclusion
 *
 * All actions emit PendingOps through `onOp`. The card itself never makes
 * network calls; the root PoolMembershipEditor batches everything into a
 * single `POST /membership:apply` on Save.
 */

import { useMemo, useState } from 'react';
import type {
  CandidateAppNode,
  CandidateProjectNode,
  CandidateVersionNode,
  GetCandidatesResponse,
  InclusionNodeKind,
  MaterializedEntry,
  PendingOp,
  PoolFlagDefaults,
  PoolInclusion,
  RefinementAction,
  RefinementNodeKind,
} from '@interlucent/organization-protocol';
import { FlagToggles } from './FlagToggles';
import styles from './InclusionCard.module.css';
import {
  type CandidateIndex,
  type DisplayRefinement,
  type StagedInclusion,
  refinementInInclusionSubtree,
} from './types';

// ---------------------------------------------------------------------------
// Effective-inclusion shape: persisted row with pending update merged on top,
// or a freshly staged inclusion. The card reads from this, not directly from
// PoolInclusion, so "what will save look like" is visible immediately.
// ---------------------------------------------------------------------------

export type EffectiveInclusion =
  | {
      kind: 'persisted';
      id: string;
      inclusion: PoolInclusion;
      /** Current values after merging pending updateInclusion ops. */
      onlyDefaultVersion: boolean;
      precacheOverride: boolean | null;
      /** @deprecated Use warmTargetOverride. Dropped in PR 5. */
      prelaunchOverride: boolean | null;
      warmTargetOverride: number | null;
      multiplexOverride: boolean | null;
      pendingUpdate: boolean;
      pendingRemoval: boolean;
    }
  | {
      kind: 'staged';
      id: string;
      staged: StagedInclusion;
      onlyDefaultVersion: boolean;
      precacheOverride: boolean | null;
      /** @deprecated Use warmTargetOverride. Dropped in PR 5. */
      prelaunchOverride: boolean | null;
      warmTargetOverride: number | null;
      multiplexOverride: boolean | null;
    };

export interface InclusionCardProps {
  effective: EffectiveInclusion;
  refinements: DisplayRefinement[];
  entries: MaterializedEntry[];      // entries whose sourceInclusionId === inclusion.id
  poolDefaults: PoolFlagDefaults;
  stale: boolean;
  candidateIndex: CandidateIndex;
  candidates: GetCandidatesResponse;
  onOp: (op: PendingOp) => void;
  onUndoStagedAdd?: () => void;       // drop the addInclusion op from pendingOps
  onUndoStagedRemoval?: () => void;   // drop the removeInclusion op from pendingOps
  onUndoStagedUpdate?: () => void;    // drop updateInclusion ops for this id
}

export function InclusionCard({
  effective,
  refinements,
  entries,
  poolDefaults,
  stale,
  candidateIndex,
  candidates,
  onOp,
  onUndoStagedAdd,
  onUndoStagedRemoval,
  onUndoStagedUpdate,
}: InclusionCardProps) {
  const [showAddException, setShowAddException] = useState(false);

  // Lookup display label from the candidate index. Falls back to node id
  // when the node has been deleted and no longer appears in the tree —
  // which is one of the ways stale inclusions manifest.
  const nodeKey = effective.kind === 'persisted'
    ? `${effective.inclusion.nodeKind}:${effective.inclusion.nodeId}`
    : `${effective.staged.nodeKind}:${effective.staged.nodeId}`;
  const entry = candidateIndex.byKey.get(nodeKey);
  const nodeKind = effective.kind === 'persisted' ? effective.inclusion.nodeKind : effective.staged.nodeKind;
  const nodeId = effective.kind === 'persisted' ? effective.inclusion.nodeId : effective.staged.nodeId;
  const label = entry?.label ?? '(node not visible in candidate tree)';

  const staged = effective.kind === 'staged';
  const pendingUpdate = effective.kind === 'persisted' && effective.pendingUpdate;
  const pendingRemoval = effective.kind === 'persisted' && effective.pendingRemoval;

  // Materialized scope-counts from server entries.
  const counts = useMemo(() => {
    const orgs = new Set<string>();
    const projects = new Set<string>();
    const apps = new Set<string>();
    const versions = new Set<string>();
    for (const e of entries) {
      const ce = candidateIndex.byKey.get(`version:${e.versionId}`);
      if (ce?.orgId) orgs.add(ce.orgId);
      if (ce?.projectId) projects.add(ce.projectId);
      apps.add(e.appId);
      versions.add(e.versionId);
    }
    return { orgs: orgs.size, projects: projects.size, apps: apps.size, versions: versions.size };
  }, [entries, candidateIndex]);

  // Exceptions targeting nodes inside this inclusion's subtree.
  const exceptionsInScope = useMemo(() => {
    return refinements.filter((r) => {
      const ref = r.kind === 'persisted' ? r.refinement : r.staged;
      return refinementInInclusionSubtree(
        { nodeKind: ref.nodeKind, nodeId: ref.nodeId },
        { nodeKind, nodeId },
        candidateIndex,
      );
    });
  }, [refinements, nodeKind, nodeId, candidateIndex]);

  const handleFlagChange = (fields: {
    precache?: boolean | null;
    prelaunch?: boolean | null;
    warmTarget?: number | null;
    multiplex?: boolean | null;
  }) => {
    const id = effective.kind === 'staged' ? effective.staged.id : effective.inclusion.id;
    onOp({
      kind: 'updateInclusion',
      id,
      payload: {
        ...(fields.precache !== undefined ? { precacheOverride: fields.precache } : {}),
        ...(fields.prelaunch !== undefined ? { prelaunchOverride: fields.prelaunch } : {}),
        ...(fields.warmTarget !== undefined ? { warmTargetOverride: fields.warmTarget } : {}),
        ...(fields.multiplex !== undefined ? { multiplexOverride: fields.multiplex } : {}),
      },
    });
  };

  const handleOnlyDefault = (next: boolean) => {
    if (effective.kind === 'staged') {
      onOp({ kind: 'updateInclusion', id: effective.staged.id, payload: { onlyDefaultVersion: next } });
    } else {
      onOp({ kind: 'updateInclusion', id: effective.inclusion.id, payload: { onlyDefaultVersion: next } });
    }
  };

  const handleRemoveInclusion = () => {
    if (effective.kind === 'staged') {
      onUndoStagedAdd?.();
      return;
    }
    onOp({ kind: 'removeInclusion', id: effective.inclusion.id });
  };

  const cardClass = [
    styles.card,
    staged ? styles.cardStaged : '',
    pendingUpdate ? styles.cardPending : '',
    pendingRemoval ? styles.cardRemoved : '',
    stale ? styles.cardStale : '',
  ].filter(Boolean).join(' ');

  return (
    <div className={cardClass}>
      <div className={styles.titleBar}>
        <div className={styles.title}>
          <span className={styles.kindBadge}>{nodeKind}</span>
          <span className={styles.name} title={label}>{label}</span>
          <span className={styles.nodeId}>{nodeId}</span>
        </div>
        <div style={{ display: 'flex', gap: 'var(--space-1)' }}>
          {staged && <span className={`${styles.statusPill} ${styles.statusStaged}`}>staged</span>}
          {pendingUpdate && <span className={`${styles.statusPill} ${styles.statusModified}`}>modified</span>}
          {pendingRemoval && <span className={`${styles.statusPill} ${styles.statusRemoval}`}>pending removal</span>}
          {stale && <span className={`${styles.statusPill} ${styles.statusStale}`}>stale</span>}
        </div>
      </div>

      <div className={styles.counts}>
        <span className={styles.countChip}>materialized<span className={styles.countNum}>{entries.length}</span>entries</span>
        <span className={styles.countChip}><span className={styles.countNum}>{counts.apps}</span>apps</span>
        <span className={styles.countChip}><span className={styles.countNum}>{counts.versions}</span>versions</span>
        {counts.projects > 0 && (
          <span className={styles.countChip}><span className={styles.countNum}>{counts.projects}</span>projects</span>
        )}
        {counts.orgs > 1 && (
          <span className={styles.countChip}><span className={styles.countNum}>{counts.orgs}</span>orgs</span>
        )}
      </div>

      <div className={styles.row}>
        <span className={styles.rowLabel}>Flags</span>
        <FlagToggles
          value={{
            precache: effective.precacheOverride,
            prelaunch: effective.prelaunchOverride,
            warmTarget: effective.warmTargetOverride,
            multiplex: effective.multiplexOverride,
          }}
          poolDefaults={poolDefaults}
          onChange={handleFlagChange}
          disabled={pendingRemoval}
        />
      </div>

      {nodeKind !== 'version' && (
        <div className={styles.defaultVersionRow}>
          <label className={styles.checkbox}>
            <input
              type="checkbox"
              checked={effective.onlyDefaultVersion}
              onChange={(e) => handleOnlyDefault(e.target.checked)}
              disabled={pendingRemoval}
            />
            Only default version
          </label>
          <span className={styles.rowLabel} style={{ color: 'var(--color-text-tertiary)' }}>
            Restrict to each covered app&rsquo;s <code>defaultVersionId</code>.
          </span>
        </div>
      )}

      <ExceptionsSection
        inclusionKind={nodeKind}
        inclusionNodeId={nodeId}
        exceptions={exceptionsInScope}
        poolDefaults={poolDefaults}
        candidates={candidates}
        candidateIndex={candidateIndex}
        showAddException={showAddException}
        setShowAddException={setShowAddException}
        onOp={onOp}
        disabled={pendingRemoval}
      />

      <div className={styles.footer}>
        {pendingUpdate && (
          <button type="button" className={styles.buttonGhost} onClick={onUndoStagedUpdate} title="Undo staged updates on this inclusion">
            Undo edits
          </button>
        )}
        {pendingRemoval ? (
          <button type="button" className={styles.buttonGhost} onClick={onUndoStagedRemoval}>
            Undo removal
          </button>
        ) : (
          <button
            type="button"
            className={styles.buttonDanger}
            onClick={handleRemoveInclusion}
            title={staged ? 'Drop this staged inclusion' : 'Remove this inclusion from the pool'}
          >
            {staged ? 'Drop' : 'Remove'}
          </button>
        )}
        {stale && (
          <button
            type="button"
            className={styles.buttonDanger}
            onClick={handleRemoveInclusion}
            title="This inclusion no longer resolves — remove it"
          >
            Reconfigure
          </button>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// ExceptionsSection — list + mini-picker for adding exceptions.
// ---------------------------------------------------------------------------

function ExceptionsSection({
  inclusionKind,
  inclusionNodeId,
  exceptions,
  poolDefaults,
  candidates,
  candidateIndex,
  showAddException,
  setShowAddException,
  onOp,
  disabled,
}: {
  inclusionKind: InclusionNodeKind;
  inclusionNodeId: string;
  exceptions: DisplayRefinement[];
  poolDefaults: PoolFlagDefaults;
  candidates: GetCandidatesResponse;
  candidateIndex: CandidateIndex;
  showAddException: boolean;
  setShowAddException: (v: boolean) => void;
  onOp: (op: PendingOp) => void;
  disabled?: boolean;
}) {
  return (
    <div className={styles.exceptions}>
      <div className={styles.exceptionsHeader}>
        <div className={styles.exceptionsTitle}>
          Exceptions {exceptions.length > 0 ? `(${exceptions.length})` : ''}
        </div>
        <button
          type="button"
          className={styles.buttonGhost}
          onClick={() => setShowAddException(!showAddException)}
          disabled={disabled || inclusionKind === 'version'}
          title={inclusionKind === 'version'
            ? 'Version inclusions have no descendants, so they cannot hold exceptions.'
            : 'Narrow or tune flags for a node inside this subtree.'}
        >
          {showAddException ? 'Cancel' : '+ Add exception'}
        </button>
      </div>

      {exceptions.length > 0 && (
        <div className={styles.exceptionsList}>
          {exceptions.map((ex) => (
            <ExceptionChip
              key={ex.kind === 'persisted' ? ex.refinement.id : ex.staged.id}
              ex={ex}
              poolDefaults={poolDefaults}
              candidateIndex={candidateIndex}
              onOp={onOp}
              disabled={disabled}
            />
          ))}
        </div>
      )}

      {showAddException && (
        <AddExceptionPicker
          inclusionKind={inclusionKind}
          inclusionNodeId={inclusionNodeId}
          candidates={candidates}
          candidateIndex={candidateIndex}
          existingExceptions={exceptions}
          onAdd={(payload) => {
            onOp({ kind: 'addRefinement', payload });
            setShowAddException(false);
          }}
        />
      )}
    </div>
  );
}

function ExceptionChip({
  ex,
  poolDefaults,
  candidateIndex,
  onOp,
  disabled,
}: {
  ex: DisplayRefinement;
  poolDefaults: PoolFlagDefaults;
  candidateIndex: CandidateIndex;
  onOp: (op: PendingOp) => void;
  disabled?: boolean;
}) {
  const ref = ex.kind === 'persisted' ? ex.refinement : ex.staged;
  const action: RefinementAction = ref.action;
  const isOverride = action === 'override';
  const label = candidateIndex.byKey.get(`${ref.nodeKind}:${ref.nodeId}`)?.label ?? ref.nodeId;
  const staged = ex.kind === 'staged';
  const chipCls = [
    styles.exception,
    isOverride ? styles.exceptionOverride : styles.exceptionExclude,
  ].join(' ');
  const actionTagCls = [
    styles.exceptionActionTag,
    isOverride ? styles.exceptionActionOverride : styles.exceptionActionExclude,
  ].join(' ');

  const handleFlagChange = (fields: {
    precache?: boolean | null;
    prelaunch?: boolean | null;
    warmTarget?: number | null;
    multiplex?: boolean | null;
  }) => {
    const id = ex.kind === 'staged' ? ex.staged.id : ex.refinement.id;
    onOp({
      kind: 'updateRefinement',
      id,
      payload: {
        ...(fields.precache !== undefined ? { precacheOverride: fields.precache } : {}),
        ...(fields.prelaunch !== undefined ? { prelaunchOverride: fields.prelaunch } : {}),
        ...(fields.warmTarget !== undefined ? { warmTargetOverride: fields.warmTarget } : {}),
        ...(fields.multiplex !== undefined ? { multiplexOverride: fields.multiplex } : {}),
      },
    });
  };

  const handleRemove = () => {
    if (ex.kind === 'persisted') {
      onOp({ kind: 'removeRefinement', id: ex.refinement.id });
    } else {
      onOp({ kind: 'removeRefinement', id: ex.staged.id });
    }
  };

  return (
    <div className={chipCls}>
      <span className={actionTagCls}>{isOverride ? '⚙ override' : '× exclude'}</span>
      <span className={styles.exceptionNodeKind}>{ref.nodeKind}</span>
      <span className={styles.exceptionLabel} title={label}>{label}</span>
      {isOverride && (
        <FlagToggles
          value={{
            precache: ref.precacheOverride,
            prelaunch: ref.prelaunchOverride,
            warmTarget: ref.warmTargetOverride,
            multiplex: ref.multiplexOverride,
          }}
          poolDefaults={poolDefaults}
          onChange={handleFlagChange}
          disabled={disabled}
          compact
        />
      )}
      {staged && <span className={`${styles.statusPill} ${styles.statusStaged}`}>staged</span>}
      <button
        type="button"
        className={styles.buttonRemoveChip}
        onClick={handleRemove}
        disabled={disabled}
        title="Remove this exception"
      >
        ×
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// AddExceptionPicker — mini-picker constrained to this inclusion's subtree.
// ---------------------------------------------------------------------------

function AddExceptionPicker({
  inclusionKind,
  inclusionNodeId,
  candidates,
  candidateIndex,
  existingExceptions,
  onAdd,
}: {
  inclusionKind: InclusionNodeKind;
  inclusionNodeId: string;
  candidates: GetCandidatesResponse;
  candidateIndex: CandidateIndex;
  existingExceptions: DisplayRefinement[];
  onAdd: (payload: {
    nodeKind: RefinementNodeKind;
    nodeId: string;
    action: RefinementAction;
    precacheOverride?: boolean | null;
    prelaunchOverride?: boolean | null;
    warmTargetOverride?: number | null;
    multiplexOverride?: boolean | null;
  }) => void;
}) {
  const [action, setAction] = useState<RefinementAction>('exclude');
  const [search, setSearch] = useState('');

  const eligibleNodes = useMemo(
    () => collectSubtreeNodes(inclusionKind, inclusionNodeId, candidates, candidateIndex),
    [inclusionKind, inclusionNodeId, candidates, candidateIndex],
  );

  const q = search.trim().toLowerCase();
  const filtered = useMemo(() => {
    if (!q) return eligibleNodes;
    return eligibleNodes.filter((n) => n.label.toLowerCase().includes(q) || n.id.toLowerCase().includes(q));
  }, [eligibleNodes, q]);

  const existingByKey = new Set(
    existingExceptions.map((e) => {
      const r = e.kind === 'persisted' ? e.refinement : e.staged;
      return `${r.nodeKind}:${r.nodeId}:${r.action}`;
    }),
  );

  return (
    <div className={styles.addExceptionPicker}>
      <div className={styles.addExceptionControls}>
        <label className={styles.checkbox}>
          <input type="radio" name="action" checked={action === 'exclude'} onChange={() => setAction('exclude')} />
          × Exclude
        </label>
        <label className={styles.checkbox}>
          <input type="radio" name="action" checked={action === 'override'} onChange={() => setAction('override')} />
          ⚙ Override
        </label>
        <input
          type="search"
          placeholder="Search this subtree…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{
            flex: 1,
            minWidth: 220,
            padding: 'var(--space-1) var(--space-2)',
            background: 'var(--color-bg-primary)',
            color: 'var(--color-text-primary)',
            border: 'var(--border-width) solid var(--color-border)',
            fontFamily: 'var(--font-sans)',
            fontSize: 'var(--text-sm)',
          }}
        />
      </div>
      <div className={styles.addExceptionList}>
        {filtered.length === 0 && (
          <div className={styles.emptyState} style={{ padding: 'var(--space-3)' }}>
            No descendants match.
          </div>
        )}
        {filtered.map((n) => {
          const dup = existingByKey.has(`${n.kind}:${n.id}:${action}`);
          return (
            <button
              type="button"
              key={`${n.kind}:${n.id}`}
              className={styles.addExceptionListRow}
              disabled={dup}
              title={dup ? 'An exception with this action already exists for this node.' : `Add ${action} on ${n.label}`}
              onClick={() =>
                onAdd({
                  nodeKind: n.kind,
                  nodeId: n.id,
                  action,
                  precacheOverride: action === 'override' ? null : undefined,
                  prelaunchOverride: action === 'override' ? null : undefined,
                  warmTargetOverride: action === 'override' ? null : undefined,
                  multiplexOverride: action === 'override' ? null : undefined,
                })
              }
            >
              <span>
                <span className={styles.exceptionNodeKind} style={{ marginRight: 'var(--space-2)' }}>{n.kind}</span>
                {n.label}
              </span>
              <span className={styles.nodeId}>{n.id}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

interface SubtreeNode {
  kind: RefinementNodeKind;
  id: string;
  label: string;
}

function collectSubtreeNodes(
  inclusionKind: InclusionNodeKind,
  inclusionNodeId: string,
  candidates: GetCandidatesResponse,
  candidateIndex: CandidateIndex,
): SubtreeNode[] {
  const out: SubtreeNode[] = [];
  const pushProject = (p: CandidateProjectNode) => {
    out.push({ kind: 'project', id: p.id, label: candidateIndex.byKey.get(`project:${p.id}`)?.label ?? p.name });
    for (const a of p.apps) pushApp(a);
  };
  const pushApp = (a: CandidateAppNode) => {
    out.push({ kind: 'app', id: a.id, label: candidateIndex.byKey.get(`app:${a.id}`)?.label ?? (a.name || a.slug) });
    for (const v of a.versions) pushVersion(v, a);
  };
  const pushVersion = (v: CandidateVersionNode, a: CandidateAppNode) => {
    out.push({ kind: 'version', id: v.id, label: `${a.name || a.slug} · ${v.id.slice(0, 10)}` });
  };

  if (inclusionKind === 'org') {
    const org = candidates.orgs.find((o) => o.id === inclusionNodeId);
    if (!org) return out;
    for (const p of org.projects) pushProject(p);
  } else if (inclusionKind === 'project') {
    for (const org of candidates.orgs) {
      const p = org.projects.find((pp) => pp.id === inclusionNodeId);
      if (p) {
        for (const a of p.apps) pushApp(a);
        break;
      }
    }
  } else if (inclusionKind === 'app') {
    for (const org of candidates.orgs) {
      for (const p of org.projects) {
        const a = p.apps.find((aa) => aa.id === inclusionNodeId);
        if (a) {
          for (const v of a.versions) pushVersion(v, a);
          return out;
        }
      }
    }
  }
  // Version inclusions: no descendants, no exceptions — picker is disabled.
  return out;
}
